import { extent, scaleLinear, scaleLog } from "d3";
import { flatten, keys, map } from "ramda";

import { type ColumnAnnotation, type DatasetColumn, type StringMapEntry } from "../graphql.js";
import { type IColumnAnnotationLookup } from "../redux/modules/dataset-viewer.js";
import {
  DatasetChart,
  type IConditionCombination,
  type IDataSeriesData,
  type IDatasetChartProps,
} from "../ui-modules/analytical-viewer/DatasetChart.js";

const LABEL_MAX_LENGTH = 30;

export function getEllipsedLabel(label: string) {
  if (!label) {
    return "";
  }
  return label.length > LABEL_MAX_LENGTH ? label.substring(0, LABEL_MAX_LENGTH - 1) + "\u2026" : label;
}

function getMaxLabelLength(data: IConditionCombination[], conditionIndex: number) {
  // We need to use reduce here to scale well with large arrays, otherwise the Math.max call will fail.
  return data
    .map(
      (c: IConditionCombination) =>
        getEllipsedLabel((c.conditions.map((cond) => cond.value)[conditionIndex] || "").toString()).length,
    )
    .reduce((a, b) => Math.max(a, b), Number.NEGATIVE_INFINITY);
}

export interface IXAxisSettings {
  bottomMargin: number;
  bucketWidth: number;
  bucketPadding: number;
  firstLabelRotated: boolean;
  firstLabelYOffset: number;
  firstLabelHeight: number;
  secondLabelYOffset: number;
  secondLabelRotated: boolean;
  secondLabelHeight: number;
  thirdLabelYOffset: number;
  thirdLabelRotated: boolean;
  thirdLabelHeight: number;
}

export function calculateXAxisSettings(
  data: IConditionCombination[],
  selectedConditionsCount: number,
  chartWidth: number,
): IXAxisSettings {
  const barCategoryGap = 0.1; // The gap between two bar categories, which can be a percent value or a fixed value. DEFAULT = 10%
  const chartWidthWithoutPadding: number = chartWidth - DatasetChart.XAXIS_CHART_PADDING * 2;
  // calculate bucket width, i.e. the plotting width for each condition combination (not including padding)
  const bucketPadding: number = data.length > 1 ? (chartWidthWithoutPadding * barCategoryGap) / (data.length - 1) : 0;
  const bucketWidth: number = (chartWidthWithoutPadding - chartWidthWithoutPadding * barCategoryGap) / data.length;

  // calculate max label length of first condition
  const maxFirstLabel: number = selectedConditionsCount > 0 ? getMaxLabelLength(data, 0) : 0;
  // if we need more space than is available we rotate the labels
  const firstLabelRotated: boolean = bucketWidth < maxFirstLabel * DatasetChart.CHAR_WIDTH_IN_PX;

  // calculate offset and height of labels in first row
  const firstLabelYOffset = 16;
  const firstLabelHeight: number = firstLabelRotated ? maxFirstLabel * DatasetChart.ROTATED_CHAR_HEIGHT_IN_PX : 16;

  /**************** SECOND LEVEL LABELS *************/
  const secondLabelYOffset = 30; // add a margin

  // calculate max label length of second condition
  const maxSecondLabel: number = selectedConditionsCount > 1 ? getMaxLabelLength(data, 1) : 0;
  // rotation of the labels will be driven by the max length and the minimum bucket width of the previous level
  let secondLabelRotated = false;

  if (selectedConditionsCount >= 2) {
    data.forEach((combination: IConditionCombination) => {
      const x1: number = -(bucketWidth / 2);
      const x2: number =
        x1 + combination.firstInnerGroupSize * bucketWidth + (combination.firstInnerGroupSize - 1) * bucketPadding;
      const secondLevelBucketWidth: number = x2 - x1;

      if (combination.conditions[1]?.value.length * DatasetChart.CHAR_WIDTH_IN_PX > secondLevelBucketWidth) {
        secondLabelRotated = true;
      }
    });
  }

  // height depends whether the labels are rotated
  const secondLabelHeight: number = secondLabelRotated ? maxSecondLabel * DatasetChart.ROTATED_CHAR_HEIGHT_IN_PX : 16;

  /**************** THIRD LEVEL LABELS *************/
  const thirdLabelYOffset = 30; // add a margin

  // calculate max label length of second condition
  const maxThirdLabel: number = selectedConditionsCount > 2 ? getMaxLabelLength(data, 2) : 0;
  let thirdLabelRotated = false;

  if (selectedConditionsCount >= 3) {
    data.forEach((combination: IConditionCombination) => {
      const x1: number = -(bucketWidth / 2);
      const x2: number =
        x1 + combination.secondInnerGroupSize * bucketWidth + (combination.secondInnerGroupSize - 1) * bucketPadding;
      const thirdLevelBucketWidth: number = x2 - x1;

      if (combination.conditions[2]?.value.length * DatasetChart.CHAR_WIDTH_IN_PX > thirdLevelBucketWidth) {
        thirdLabelRotated = true;
      }
    });
  }

  const thirdLabelHeight: number = thirdLabelRotated ? maxThirdLabel * DatasetChart.ROTATED_CHAR_HEIGHT_IN_PX : 16;

  // calculate space required for showing the x-axis description and optionally the x-axis labels
  let bottomMargin: number = DatasetChart.AXIS_LABEL_HEIGHT;

  if (bucketWidth >= DatasetChart.MIN_BUCKET_WIDTH) {
    // increase the total bottom margin according to the number of conditions and their respective offsets and heights
    bottomMargin += selectedConditionsCount >= 1 ? firstLabelHeight + firstLabelYOffset : 0;
    bottomMargin += selectedConditionsCount >= 2 ? secondLabelHeight + secondLabelYOffset : 0;
    bottomMargin += selectedConditionsCount >= 3 ? thirdLabelHeight + thirdLabelYOffset : 0;
  }

  return {
    bottomMargin,
    bucketWidth,
    bucketPadding,
    firstLabelRotated,
    firstLabelYOffset,
    firstLabelHeight,
    secondLabelYOffset,
    secondLabelRotated,
    secondLabelHeight,
    thirdLabelYOffset,
    thirdLabelRotated,
    thirdLabelHeight,
  };
}

export function getYAxisDomain(data: IConditionCombination[], isLog: boolean): [any, any] {
  // calculate flattened array of all data points in absolute values
  const rawData = flatten(
    map((chartConditionComb: IConditionCombination) => {
      let res: number[] = [];

      keys(chartConditionComb.dataseries).forEach((dataSeriesName: string) => {
        const series: IDataSeriesData = chartConditionComb.dataseries[dataSeriesName];

        res = res.concat(series.rawData.map((raw) => (isLog ? Math.max(raw.value, 0) : raw.value)));

        /*
        when no selection is made in the table we do not need to display any raw data point on the chart therefore
         we might want to set the domain from the error bars only
               const lowerBound: number = series.consolidatedValue - series.error[0];
               const upperBound: number = series.consolidatedValue + series.error[1];
       */
      }, data);

      return res;
    }, data),
  );

  if (!isLog) {
    rawData.push(0);
  }

  // The raw domain might contain numbers with many significant digits due to rounding effects
  const rawDomain: [number, number] = extent(rawData);

  // Use d3 scale to calculate a nice domain for the lin case (rounding can still happen later in CustomYAxisTick)
  return (isLog ? scaleLog().domain(rawDomain) : scaleLinear().domain(rawDomain)).nice().domain() as [number, number];
}

// the complexity indicator provide a clue about the processing time for building the chart data.
// it combines the count of x-axis ticks (=nb of combinations of conditions) with the count of columns/samples
export function calculateComplexityIndicator(chartProps: IDatasetChartProps): number {
  let conditionsCombinationsCount = 0;

  chartProps.selectedAnnotations.forEach((annotation: ColumnAnnotation) => {
    conditionsCombinationsCount = (conditionsCombinationsCount || 1) * annotation.valueSet.length;
  });

  return conditionsCombinationsCount * chartProps.columns.length;
}

export function createAnnotationLookupByKey(column: DatasetColumn): IColumnAnnotationLookup {
  const lookup: IColumnAnnotationLookup = {};

  column.annotations.forEach((annotation: StringMapEntry) => {
    // with multi values, it is possible that multiple annotations get the same key, therefore the lookup targets a list
    lookup[annotation.key!] = [...(lookup[annotation.key!] ?? []), annotation];
  });

  return lookup;
}
